import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import public components
import LoginPage from './components/public/LoginPage';
import BookingPage from './components/public/BookingPage';
import PublicPortfolio from './components/public/PublicPortfolio';

// Import admin Dashboard (which imports all other admin components)
import Dashboard from './components/admin/Dashboard';

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const sessionTimeoutRef = React.useRef(null);
  const warningTimeoutRef = React.useRef(null);
  const countdownIntervalRef = React.useRef(null);

  // Session timeout settings
  const SESSION_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
  const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before timeout

  const [countdown, setCountdown] = useState(WARNING_TIME / 1000); // Warning time in seconds

  const clearTimeouts = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
  };

  const startSessionTimer = () => {
    clearTimeouts();

    // Set warning timeout (8 hours - 5 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setCountdown(WARNING_TIME / 1000); // Reset countdown to warning time in seconds

      // Start countdown interval
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set auto-logout timeout (8 hours)
    sessionTimeoutRef.current = setTimeout(() => {
      logout();
    }, SESSION_TIMEOUT);
  };

  const resetSessionTimer = React.useCallback(() => {
    if (user && !showTimeoutWarning) {
      startSessionTimer();
      localStorage.setItem('loginTime', Date.now().toString());
    }
  }, [user, showTimeoutWarning]);

  const extendSession = () => {
    setShowTimeoutWarning(false);
    setCountdown(WARNING_TIME / 1000);
    startSessionTimer();
    localStorage.setItem('loginTime', Date.now().toString());
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const loginTime = localStorage.getItem('loginTime');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Check if session is still valid
      if (loginTime) {
        const elapsed = Date.now() - parseInt(loginTime);
        if (elapsed >= SESSION_TIMEOUT) {
          // Session expired
          logout();
        } else {
          // Start timer for remaining session time
          const remainingTime = SESSION_TIMEOUT - elapsed;
          const remainingWarningTime = remainingTime - WARNING_TIME;

          if (remainingWarningTime > 0) {
            warningTimeoutRef.current = setTimeout(() => {
              setShowTimeoutWarning(true);
            }, remainingWarningTime);
          } else {
            // Warning time has already passed
            setShowTimeoutWarning(true);
          }

          sessionTimeoutRef.current = setTimeout(() => {
            logout();
          }, remainingTime);
        }
      } else {
        // No login time stored, start fresh timer
        localStorage.setItem('loginTime', Date.now().toString());
        startSessionTimer();
      }
    }
    setLoading(false);

    // Cleanup on unmount
    return () => {
      clearTimeouts();
    };
  }, []);

  // Track user activity to reset session timer
  useEffect(() => {
    if (!user || showTimeoutWarning) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    events.forEach(event => {
      window.addEventListener(event, resetSessionTimer);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetSessionTimer);
      });
    };
  }, [user, showTimeoutWarning, resetSessionTimer]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('loginTime', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    startSessionTimer();
  };

  const logout = () => {
    clearTimeouts();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('loginTime');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setShowTimeoutWarning(false);
  };

  // Format countdown as MM:SS
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <Router>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
          <Route path="/book" element={<BookingPage />} />
          <Route path="/portfolio" element={<PublicPortfolio />} />
          <Route path="/dashboard/*" element={user ? <Dashboard /> : <Navigate to="/login" />} />
          <Route path="/" element={<Navigate to="/book" />} />
        </Routes>
      </Router>

      {/* Session Timeout Warning Modal */}
      {showTimeoutWarning && user && (
        <div className="modal" style={{ zIndex: 10000 }}>
          <div className="modal-content">
            <h3>Session Timeout Warning</h3>
            <p>Your session will expire in {formatCountdown(countdown)}.</p>
            <p>Would you like to extend your session?</p>
            <div className="modal-actions">
              <button onClick={extendSession} className="btn-primary">
                Extend Session
              </button>
              <button onClick={logout} className="btn-secondary">
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export default App;
